<template>
  <div class="ps-text-field">
    <div v-if="label" class="pb-2">{{ $t(label) }}</div>
    <v-text-field
      ref="input"
      v-model="internalValue"
      @input="onAmountInput"
      @blur="onAmountBlur"
      @keydown="onKeyDown"
      v-bind="$attrs"
      outlined
      flat
      hide-details
    >
      <template v-for="(_, slot) of $slots" :slot="slot">
        <slot :name="slot" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [String, Number],
      default: "0",
    },
    label: {
      type: String,
      default: "",
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      amount: 0,
      formattedAmount: "0",
    };
  },
  computed: {
    // When used as a plain number input (type="number"), skip comma grouping —
    // browsers reject "1,200" in a number field and wipe the value (stock bug).
    isNumberType() {
      return (this.$attrs && this.$attrs.type) === "number";
    },
    internalValue: {
      get() {
        return this.formattedAmount;
      },
      set(value) {
        this.formattedAmount = value;
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue !== this.amount) {
          this.amount = parseFloat(newValue) || 0;
          this.formattedAmount = this.formatNumber(this.amount);
        }
      },
    },
  },
  methods: {
    onAmountInput(value) {
      // Remove all non-numeric characters except decimal point
      let cleanValue = value.replace(/[^0-9.]/g, "");

      // Ensure only one decimal point
      const parts = cleanValue.split(".");
      if (parts.length > 2) {
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      // Limit decimal places to 2 digits maximum
      const updatedParts = cleanValue.split(".");
      if (updatedParts.length === 2 && updatedParts[1].length > 2) {
        cleanValue = updatedParts[0] + "." + updatedParts[1].substring(0, 2);
      }

      // Update the raw amount value
      this.amount = parseFloat(cleanValue) || 0;

      // Set the formatted amount to the clean value (this will be the display value)
      this.formattedAmount = cleanValue;

      // Format with commas if it's a valid number
      if (cleanValue && cleanValue !== "0" && cleanValue !== "") {
        const numValue = parseFloat(cleanValue);
        if (!isNaN(numValue)) {
          this.formattedAmount = this.formatNumberWithDecimals(cleanValue);
        }
      }

      // Emit the numeric value to parent
      this.$emit("input", this.amount);
    },
    onAmountBlur() {
      // Format the number with commas when user finishes editing
      if (this.amount > 0) {
        this.formattedAmount = this.formatNumber(this.amount);
      } else {
        this.formattedAmount = "0";
      }
      // Emit the final value to parent
      this.$emit("input", this.amount);
    },

    onKeyDown(event) {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if (
        [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !==
          -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true)
      ) {
        return;
      }

      // Ensure that it is a number and stop the keypress
      if (
        (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
        (event.keyCode < 96 || event.keyCode > 105) &&
        event.keyCode !== 190 &&
        event.keyCode !== 110
      ) {
        event.preventDefault();
        return;
      }

      // Check for decimal point
      if (event.keyCode === 190 || event.keyCode === 110) {
        // Check if decimal point already exists
        if (this.formattedAmount.indexOf(".") !== -1) {
          event.preventDefault();
          return;
        }
      }

      // Check for decimal places limit
      const currentValue = this.formattedAmount;
      const decimalIndex = currentValue.indexOf(".");
      if (decimalIndex !== -1) {
        const decimalPart = currentValue.substring(decimalIndex + 1);
        if (decimalPart.length >= 2) {
          event.preventDefault();
          return;
        }
      }
    },
    formatNumber(num) {
      // Format number with commas and preserve decimals
      if (this.isNumberType) {
        return String(num);
      }
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatNumberWithDecimals(value) {
      // Format number with commas while preserving the exact decimal places from input
      if (this.isNumberType) {
        return value;
      }
      const parts = value.split(".");
      if (parts.length === 2) {
        // Format the integer part with commas
        const integerPart = parseInt(parts[0]).toLocaleString("en-US");
        return integerPart + "." + parts[1];
      } else {
        // No decimal point, just format the integer part
        return parseInt(value).toLocaleString("en-US");
      }
    },
    focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    select() {
      if (this.$refs.input && this.$refs.input.$el) {
        const input = this.$refs.input.$el.querySelector("input");
        if (input) {
          input.select();
        }
      }
    },
  },
};
</script>

<style></style>
